import { memo, useCallback, useEffect, useState } from "react";
import { TextField } from "ui/components/TextField/TextField";
import { NumberField } from "ui/components/TextField/NumberField";
import { DatePicker } from "ui/components/DatePicker";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { InvoiceIncomingSearchFilterProps } from "./types";
import { InvoiceIncomingSearchFormValues } from "services/api/finance/incoming-invoices/types";
import {
  initialInvoiceIncomingSearchForm,
  INCOMING_SOURCE_LABELS,
  INVOICE_ALLOCATION_STATUS_LABELS,
} from "services/api/finance/incoming-invoices/consts";
import { getInvoiceIncomingStatusesAPI } from "services/api/finance/incoming-invoices/api";
import {
  useHandleTextFieldChange,
  useHandleDropdownChange,
  useHandleDateChange,
  useHandleNumberFieldChange,
} from "services/forms/hooks";
import useUrlFilters from "services/hooks/useUrlFilters";

const sourceOptions: DropdownOption[] = Object.entries(INCOMING_SOURCE_LABELS).map(([value, text]) => ({
  value,
  text,
}));

const allocationOptions: DropdownOption[] = Object.entries(INVOICE_ALLOCATION_STATUS_LABELS).map(([value, text]) => ({
  value,
  text,
}));

const InvoiceIncomingSearchFilters: React.FC<InvoiceIncomingSearchFilterProps> = ({ onSearchChange }) => {
  // Datumski raspon (Račun od/do) sinkroniziran s URL-om — drill-down s dashboarda + share/reload.
  const [formValues, setFormValues] = useUrlFilters<InvoiceIncomingSearchFormValues>(
    initialInvoiceIncomingSearchForm,
    {
      invoiceDateFrom: { param: "invoiceDateFrom", type: "date" },
      invoiceDateTo: { param: "invoiceDateTo", type: "date-end" },
    }
  );
  const [statusOptions, setStatusOptions] = useState<DropdownOption[]>([]);

  useEffect(() => {
    fetchStatuses();
  }, []);

  useEffect(() => {
    onSearchChange(formValues);
  }, [formValues]);

  const fetchStatuses = async () => {
    try {
      const statuses = await getInvoiceIncomingStatusesAPI();
      setStatusOptions(statuses.map((s: any) => ({ text: s.name, value: s.id })));
    } catch {}
  };

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);
  const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);
  const handleDateChange = useHandleDateChange(setFormValues, formValues);
  const handleNumberFieldChange = useHandleNumberFieldChange(setFormValues, formValues);

  const handleResetFormClick = useCallback(() => {
    setFormValues(initialInvoiceIncomingSearchForm);
  }, []);

  return (
    <div className="flex flex-wrap items-end gap-2 mb-3">
      <div className="w-[150px]">
        <TextField
          type="text"
          name="search"
          value={formValues.search}
          onChange={handleTextFieldChange}
          placeholder="Pretraži..."
          startIcon="/media/icons/search.svg"
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[150px]">
        <Dropdown
          name="statusId"
          value={formValues.statusId}
          options={statusOptions}
          onChange={handleDropdownChange}
          placeholder="Status"
        />
      </div>
      <div className="w-[120px]">
        <Dropdown
          name="incomingSourceId"
          value={formValues.incomingSourceId}
          options={sourceOptions}
          onChange={handleDropdownChange}
          placeholder="Izvor"
        />
      </div>
      <div className="w-[150px]">
        <Dropdown
          name="allocationStatus"
          value={formValues.allocationStatus}
          options={allocationOptions}
          onChange={handleDropdownChange}
          placeholder="Raspodjela"
        />
      </div>
      <div className="w-[120px]">
        <DatePicker
          name="invoiceDateFrom"
          label="Račun od - do"
          value={formValues.invoiceDateFrom}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[120px]">
        <DatePicker
          name="invoiceDateTo"
          value={formValues.invoiceDateTo}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[120px]">
        <DatePicker
          name="dueDateFrom"
          label="Dospijeće od - do"
          value={formValues.dueDateFrom}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[120px]">
        <DatePicker
          name="dueDateTo"
          value={formValues.dueDateTo}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[90px]">
        <NumberField
          name="totalMin"
          label="Iznos od - do"
          placeholder="Iznos od"
          value={formValues.totalMin}
          onChange={handleNumberFieldChange}
          decimals={2}
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[90px]">
        <NumberField
          name="totalMax"
          placeholder="do"
          value={formValues.totalMax}
          onChange={handleNumberFieldChange}
          decimals={2}
          cssClassName="bg-white"
        />
      </div>
      <span
        onClick={handleResetFormClick}
        className="text-xs font-bold cursor-pointer text-gray-500 hover:text-sky-600 pb-2 whitespace-nowrap"
      >
        RESET
      </span>
    </div>
  );
};

export default memo(InvoiceIncomingSearchFilters);
